import { enableSSOSchema, EnableSSOFormData } from '../schemas/ssoSchemas';

export interface ValidationResult {
  success: boolean;
  data?: EnableSSOFormData;
  errors?: Record<string, string>;
}

/**
 * Service responsible for SSO form validation.
 * Encapsulates validation logic separate from UI components.
 */
export class SSOFormValidator {
  /**
   * Validates SSO form data against the schema.
   * Returns success status with either validated data or error messages.
   */
  public validate(formValues: unknown): ValidationResult {
    const validation = enableSSOSchema.safeParse(formValues);

    if (!validation.success) {
      const errors: Record<string, string> = {};
      validation.error.errors.forEach((err) => {
        if (err.path[0]) {
          errors[err.path[0].toString()] = err.message;
        }
      });

      return {
        success: false,
        errors,
      };
    }

    return {
      success: true,
      data: validation.data,
    };
  }

  /**
   * Checks if the form values represent a re-enable operation.
   * Re-enable means all credential fields are empty (keeping existing credentials).
   */
  public isReEnableOperation(formValues: EnableSSOFormData): boolean {
    return (
      !formValues.entraIdTenantId &&
      !formValues.entraIdClientId &&
      !formValues.entraIdClientSecret
    );
  }

  /**
   * Determines if a field should be required based on SSO status.
   * For new connections: all fields required.
   * For re-enabling: fields are optional.
   */
  public isFieldRequired(
    fieldName: string,
    hasDisabledConnection: boolean,
  ): boolean {
    const requiredFields = [
      'entraIdTenantId',
      'entraIdClientId',
      'entraIdClientSecret',
    ];

    if (!requiredFields.includes(fieldName)) {
      return false;
    }

    // If there's a disabled connection, fields are optional (for re-enable)
    return !hasDisabledConnection;
  }
}
